<template>
  <div>
    <validation-observer ref="customer" tag="div">
      <b-form-group
          :label="lodash.upperFirst($t('customers.type'))"
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
      >
        <template v-for="(typeOption, index) in typeOptions">
          <b-button
              :key="typeOption.value"
              :class="typeButtonClass(index)"
              :variant="typeButtonVariant(typeOption.value)"
              disabled
              type="button"
          >
            <i v-if="model.types.includes(typeOption.value)" class="fa fa-check me-1"/>
            {{ typeOption.text }}
          </b-button>
        </template>
      </b-form-group>
      <div class="row">
        <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('customers.partner_name'))"
            class="col-md-6"
            rules="required"
            vid="name"
        >
          <b-form-group
              :invalid-feedback="errors[0]"
              :label="lodash.startCase($t('customers.partner_name'))"
              :state="errors[0] ? false : validated ? true : null"
              class="row mb-3  asterisk-after-text"
              label-cols-md="12"
          >
            <b-form-input
                id="name"
                v-model="model.name"
                :state="errors[0] ? false : validated ? true : null"
                name="name"
                type="text"
            ></b-form-input>
          </b-form-group>
        </validation-provider>
        <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('customers.code'))"
            class="col-md-6"
            rules="required"
            vid="code"
        >
          <b-form-group
              :invalid-feedback="errors[0]"
              :label="lodash.startCase($t('customers.code'))"
              :state="errors[0] ? false : validated ? true : null"
              class="row mb-3  asterisk-after-text"
              label-cols-md="12"
          >
            <b-form-input
                id="code"
                v-model="model.code"
                :state="errors[0] ? false : validated ? true : null"
                name="code"
                type="text"
            ></b-form-input>
          </b-form-group>
        </validation-provider>
        <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('customers.phone_number'))"
            class="col-md-6"
            rules="required"
            vid="phone_number"
        >
          <b-form-group
              :invalid-feedback="errors[0]"
              :label="lodash.startCase($t('customers.phone_number'))"
              :state="errors[0] ? false : validated ? true : null"
              class="row mb-3 asterisk-after-text"
              label-cols-md="12"
          >
            <b-form-input
                id="phone_number"
                v-model="model.phone_number"
                :state="errors[0] ? false : validated ? true : null"
                name="phone_number"
                type="number"
            ></b-form-input>
          </b-form-group>
        </validation-provider>
        <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.upperFirst($t('odoo_partners.singular'))"
            class="col-md-6"
            rules="required"
            vid="odoo_partner_id">
          <b-form-group
              :invalid-feedback="errors[0]"
              :state="errors[0] ? false : (validated ? true : null)"
              class="row mb-3  asterisk-after-text"
              label="Vendor di Odoo"
              label-cols-md="12"
          >
            <multiselect-ajax
                :key="`${_uid}-odoo-partners`"
                v-model.number="model.odoo_partner_id"
                :custom-label="odooPartnerLabel"
                :params="odooPartnerFetchParameters"
                name="odoo_partner_id"
                url="/odoo/partners"
            >
              <template v-slot:beforeList>
                <b-button v-b-modal.ticket-create-modal variant="link"
                >+ {{ $t('Add new {something}', {something: $t('odoo_partners.singular')}) }}
                </b-button>
                <odoo-partner-create-modal
                    :id="`ticket-create-modal`"
                    :key="`odoo-partner-create-modal-${model.odoo_partner_id}`"
                    @created="onCreatedOdooPartner"
                ></odoo-partner-create-modal>
              </template>
            </multiselect-ajax>
          </b-form-group>
        </validation-provider>
      </div>
      <b-tabs
          v-if="model.types.length"
          v-model="activeTypeTabIndex"
          active-nav-item-class="custom-active-nav"
          active-tab-class="p-3 border-tab"
          class="mb-3"
      >
        <b-tab
            v-for="(type) in model.types"
            :key="type"
            :title="`${lodash.startCase($t('customers.address_details'))} ${$t(`customers.types.${type}`)}`"
            card
        >
          <validation-observer v-if="type === 'farmer'" ref="farmer">
            <customer-farm
                v-model="model.farms"
                :disable-existing-use-jala-farm="true"
                :disable-region-select-when-farm-fetched="true"
                :disable-address-when-village-selected="true"
            ></customer-farm>
          </validation-observer>
          <validation-observer v-if="type === 'partner'" ref="partner">
            <customer-office
                v-model="model.partner"
                :disable-address-when-village-selected="true"
                :disable-region-select-when-village-selected="true"
                :location-card-title="`${$t('customers.location')} ${$t('customers.types.partner')}`"
            ></customer-office>
          </validation-observer>
          <validation-observer v-if="type === 'processing'" ref="processing">
            <customer-office
                v-model="model.processing"
                :disable-address-when-village-selected="true"
                :disable-region-select-when-village-selected="true"
                :location-card-title="`${$t('customers.location')} ${$t('customers.types.processing')}`"
            ></customer-office>
          </validation-observer>
        </b-tab>
      </b-tabs>
      <customer-bank-accounts
          v-model="model.bank_accounts"
          :is-modal="isModal"
      ></customer-bank-accounts>
    </validation-observer>
  </div>
</template>

<script>
import FormModel from "./model";
import CustomerBankAccounts from "@/components/forms/customer/customer-bank-accounts.vue";
import CustomerFarm from "@/components/forms/customer/customer-farm";
import CustomerOffice from "@/components/forms/customer/customer-office";
import OdooPartnerCreateModal from "@/components/modals/odoo-partner-create.vue";

export default {
  extends: FormModel,
  components: {
    CustomerBankAccounts,
    CustomerFarm,
    CustomerOffice,
    OdooPartnerCreateModal,
  },
  data() {
    return {
      region: null,
      model: {
        total_person: 0,
        region_full_name: null,
        phone_number: null,
        address: {
          street: null,
        },
        odoo_res_partner: {
          odoo_id: null,
        },
        ...this.value,
        types: this.value.types || [],
        joinedTypes: this.value.types.join(","),
        finance_account: !this.lodash.isEmpty(this.value.finance_account) ? {...this.value.finance_account} : {distress_status: 'healthy'},
        partner: this.value.partner || {
          latitude: null,
          longitude: null,
          get coordinate() {
            return this.latitude ? `${this.latitude},${this.longitude}` : null;
          },
          set coordinate(value) {
            if (value) {
              const [lat, lng] = (value || "").split(",");
              if (lat != null && Number(lat) && lng != null && Number(lng)) {
                this.latitude = Number(lat);
                this.longitude = Number(lng);
              }
            } else {
              this.latitude = null;
              this.longitude = null;
            }
          },
        },
        processing: this.value.processing || {
          latitude: null,
          longitude: null,
          get coordinate() {
            return this.latitude ? `${this.latitude},${this.longitude}` : null;
          },
          set coordinate(value) {
            if (value) {
              const [lat, lng] = (value || "").split(",");
              if (lat != null && Number(lat) && lng != null && Number(lng)) {
                this.latitude = Number(lat);
                this.longitude = Number(lng);
              }
            } else {
              this.latitude = null;
              this.longitude = null;
            }
          },
        },
        farms: this.value.farms || [
          {
            use_jala_farm: true,
            get coordinate() {
              return this.latitude
                  ? `${this.latitude},${this.longitude}`
                  : null;
            },
            set coordinate(value) {
              if (value) {
                const [lat, lng] = (value || "").split(",");
                if (lat != null && Number(lat) && lng != null && Number(lng)) {
                  this.latitude = Number(lat);
                  this.longitude = Number(lng);
                }
              } else {
                this.latitude = null;
                this.longitude = null;
              }
            },
          },
        ],
        bank_accounts: [
          ...((this.value.bank_accounts || []).length
              ? this.value.bank_accounts
              : this.value.id
                  ? []
                  : []),
        ],
      },
      activeTypeTabIndex: 0,
      typeOptions: [
        {text: this.$t("customers.types.farmer"), value: "farmer", sequence: 1,},
        {text: this.$t("customers.types.partner"), value: "partner", sequence: 2,},
        {text: this.$t("customers.types.processing"), value: "processing", sequence: 3,},
      ],
    };
  },
  watch: {
    "model.region_id": function (newId) {
      if (newId && this.region)
        this.model.region_full_name = this.region.full_name;
    },
  },
  computed: {
    isModal() {
      return this.$parent.$parent.$options.name === "BModal";
    },
    odooPartnerFetchParameters() {
      return {
        company_id: process.env.VUE_APP_ODOO_COMPANY_ID,
      };
    },
  },
  methods: {
    onRegionUpdated(value) {
      this.$set(this.model, "region_full_name", value.full_name);
    },
    onFarmRegionUpdated(farm, value) {
      this.$set(farm, "region_full_name", value.full_name);
    },
    onFarmSelected(farm, value) {
      if (!this.model.id) {
        this.$set(farm, "address", value.address);
      }
      this.$set(farm, "name", value.name);
    },
    addFarm() {
      this.model.farms.push({
        name: "",
        code: "",
        use_jala_farm: true,
        jala_farm_id: null,
        country_id: "ID",
        currency_id: "IDR",
        timezone: this.$moment().format("Z"),
        latitude: null,
        longitude: null,
        get coordinate() {
          return this.latitude ? `${this.latitude},${this.longitude}` : null;
        },
        set coordinate(value) {
          if (value) {
            const [lat, lng] = (value || "").split(",");
            if (lat != null && Number(lat) && lng != null && Number(lng)) {
              this.latitude = Number(lat);
              this.longitude = Number(lng);
            }
          } else {
            this.latitude = null;
            this.longitude = null;
          }
        },
      });
    },
    setActiveTab(index) {
      this.$set(this, 'activeTypeTabIndex', index)
    },
    isTabValid(type) {
      const validatorRef = `${type}_validation_observer`;
      if (!this.$refs[validatorRef]) {
        return Promise.resolve(true);
      }

      return this.$refs[validatorRef].validate();
    },
    validateForm(refName = "customer") {
      const target = this.$refs[refName];
      if (!target) {
        return Promise.resolve(true);
      }
      const observer = Array.isArray(target) ? target[0] : target;
      if (typeof observer.validate !== "function") {
        return Promise.resolve(true);
      }
      return observer.validate();
    },
    validateTabs(types) {
      return Promise.all(
          types.map(async (type, index) => {
            return this.isTabValid(type, index);
          })
      );
    },
    ensureActiveTabForInvalidFeedback(invalidFeedback) {
      if (!invalidFeedback) {
        return Promise.resolve();
      }
      const tabPane = invalidFeedback.closest('[data-tab-type]');
      if (!tabPane) {
        return Promise.resolve();
      }
      const tabType = tabPane.getAttribute("data-tab-type");
      const targetIndex = this.model.types.indexOf(tabType);
      if (targetIndex === -1 || targetIndex === this.activeTypeTabIndex) {
        return Promise.resolve();
      }
      this.activeTypeTabIndex = targetIndex;
      return this.$nextTick();
    },
    validateAll() {
      let validateForms = [
        this.validateForm('customer'),
        ...this.model.types.map(type => this.validateForm(type))
      ]

      return Promise.all(validateForms).then(isValids => {
        let isAllValid = isValids.every(value => value === true);
        if (isAllValid) this.emitValue();
        this.$emit('update:is-valid', isAllValid)
        this.$emit('update:form-errors', this.$refs.customer.errors)
        let isTabValids = isValids.slice(1);
        for (let i = 0; i < isTabValids.length; i++) {
          if (isTabValids[i] === false) {
            this.activeTypeTabIndex = i;
            break;
          }
        }
        this.$nextTick(() => {
          const invalidFeedback =
              this.$el.querySelector(".tab-pane.active .invalid-feedback") ||
              this.$el.querySelector(".invalid-feedback");
          if (invalidFeedback) {
            invalidFeedback.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
        return isValids;
      })
    },
    odooPartnerLabel({name, is_company}) {
      return is_company ? `${name} (${this.lodash.upperFirst(this.$t('odoo_partners.company'))})` :
          `${name} (${this.lodash.upperFirst(this.$t('odoo_partners.individual'))})`
    },
    onCreatedOdooPartner({id}) {
      this.$set(this.model, 'odoo_partner_id', id)
    },
    validateTypeSelection() {
      this.$nextTick(() => {
        if (this.$refs.typesProvider) {
          this.$refs.typesProvider.validate();
        }
      });
    },
    toggleType(type) {
      let isIncluded = this.model.types.includes(type);
      if (isIncluded) {
        this.model.types.splice(this.model.types.indexOf(type), 1);
      } else {
        this.model.types.push(type);
      }
      this.model.joinedTypes = this.model.types.join(",");
      this.validateTypeSelection();
    },
    typeButtonDisabled(type) {
      if (type === "farmer" && this.model.types.includes("processing")) {
        return true;
      }
      return type === "processing" && this.model.types.includes("farmer");

    },
    typeButtonClass(index) {
      return {
        "ms-1": index !== 0,
        "me-1": index !== this.typeOptions.length - 1,
      };
    },
    typeButtonVariant(type) {
      return this.model.types.includes(type) ? "primary" : "white";
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .custom-checkbox .custom-control-label::before {
    border-radius: 0.25rem;
  }

  .col-form-label {
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
    color: #6c7589;
  }
}
</style>

<style lang="scss" scoped>

.section-title {
  font-weight: 800;
  font-size: 16px;
  line-height: 19px;
  letter-spacing: 0.05em;
  color: #0084f3;
}

::v-deep {
  table {
    thead {
      background: #f9fafb !important;
    }
  }

  .border-tab {
    border: 1px solid #D0D5DD;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .nav-link {
    font: 600 14px/20px 'Inter', 'sans-serif';
    color: black;

    &.custom-active-nav {
      border-bottom: 2px solid #0083F2 !important;
      color: #0083F2 !important;
    }
  }

  .vs__selected-options input {
    padding: 4px 16px;
  }
}

.card-form {
  max-width: 720px;
}
</style>

<style lang="scss">
.vs__dropdown-menu {
  z-index: 9999 !important;
}

.button-add-bank {
  transition: background .2s ease-in;
  border-radius: 0;

  &:hover {
    background: #EEF3F7;
  }
}
</style>
